import { useCallback } from 'react';
import { useAuth } from '@/contexts/integrated-auth';
import { ChatMessageList } from './chat-message-list';
import { ChatInput } from './chat-input';
import { usePublicChat, type GifData, type TrackData, type LeagueData } from './hooks/use-public-chat';

interface CommunityChatViewProps {
  channelId: number;
  channelName?: string;
  categoryId?: number | null;
  categoryType?: string | null;
  isMember?: boolean;
}

export function CommunityChatView({
  channelId,
  channelName,
  categoryId = null,
  categoryType = null,
  isMember = false,
}: CommunityChatViewProps) {
  const { user, isAuthenticated } = useAuth();

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    deleteMessage,
    isSending,
  } = usePublicChat({
    channelId,
    userId: user?.id || null,
    username: user?.username || user?.firstName || null,
    avatarUrl: user?.avatarImageUrl || null,
    enabled: isAuthenticated,
  });

  const handleSendMessage = useCallback(
    async (content: string) => {
      await sendMessage({ content, contentType: 'text' });
    },
    [sendMessage]
  );

  const handleSendGif = useCallback(
    async (gifData: GifData, comment?: string) => {
      await sendMessage({ content: comment || '', contentType: 'gif', gifData });
    },
    [sendMessage]
  );

  const handleSendTrack = useCallback(
    async (trackData: TrackData, comment?: string) => {
      await sendMessage({ content: comment || '', contentType: 'track', trackData });
    },
    [sendMessage]
  );

  const handleSendLeague = useCallback(
    async (leagueData: LeagueData, comment?: string) => {
      await sendMessage({ content: comment || '', contentType: 'league', leagueData });
    },
    [sendMessage]
  );

  const handleDelete = useCallback(
    async (messageId: string) => {
      await deleteMessage(messageId);
    },
    [deleteMessage]
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        <ChatMessageList
          messages={messages}
          isLoading={isLoading}
          error={error}
          currentUserId={user?.id || null}
          isOwner={false}
          onReport={() => {}}
          onDelete={handleDelete}
        />
      </div>
      <div className="px-2 pt-2 pb-2">
        <ChatInput
          onSendMessage={handleSendMessage}
          onSendGif={handleSendGif}
          onSendTrack={handleSendTrack}
          onSendLeague={handleSendLeague}
          isSending={isSending}
          disabled={!isAuthenticated || !isMember}
          disabledReason={!isAuthenticated ? 'Sign in to chat.' : 'Join this room to chat and keep it in your list.'}
          communityContext={{
            channelId,
            channelName: channelName || 'Community Room',
            categoryId,
            categoryType,
          }}
        />
      </div>
    </div>
  );
}
